import { get, put } from './apiHelper.ts'
import { ALERT_PREFERENCES_URL } from './urls.ts'

export interface IAlertChannel {
    sms: boolean
    email: boolean
    push: boolean
}

export interface IAlertPreference {
    alertCode: string
    channels: IAlertChannel
}

export interface IAlertPreferenceDoc {
    _id?: string
    userId?: string
    userType?: 'owner' | 'contact'
    deviceId?: string
    alerts: IAlertPreference[]
    createdAt?: string
    updatedAt?: string
}

/**
 * Get the authenticated user's alert preferences for a device.
 * Returns the preference doc, or a shell with empty alerts array if none set.
 */
export const getMyAlertPreferences = async (deviceId: string): Promise<IAlertPreferenceDoc | null> => {
    try {
        const response = await get(`${ALERT_PREFERENCES_URL}/${deviceId}`)
        return response?.data ?? null
    } catch {
        return null
    }
}

/**
 * Update the authenticated user's alert preferences for a device.
 * Sends the COMPLETE list of alerts the user wants (full replacement).
 */
export const updateMyAlertPreferences = async (
    deviceId: string,
    alerts: IAlertPreference[]
): Promise<IAlertPreferenceDoc | null> => {
    try {
        const response = await put(`${ALERT_PREFERENCES_URL}/${deviceId}`, { alerts })
        return response?.data ?? null
    } catch {
        return null
    }
}

/**
 * Get a specific contact's alert preferences for a device.
 * Owner-only endpoint.
 */
export const getContactAlertPreferences = async (
    deviceId: string,
    contactId: string
): Promise<IAlertPreferenceDoc | null> => {
    try {
        const response = await get(`${ALERT_PREFERENCES_URL}/${deviceId}/contacts/${contactId}`)
        return response?.data ?? null
    } catch {
        return null
    }
}

/**
 * Update a contact's alert preferences for a device.
 * Owner-only endpoint. Sends the COMPLETE list of alerts.
 */
export const updateContactAlertPreferences = async (
    deviceId: string,
    contactId: string,
    alerts: IAlertPreference[]
): Promise<IAlertPreferenceDoc | null> => {
    try {
        const response = await put(`${ALERT_PREFERENCES_URL}/${deviceId}/contacts/${contactId}`, { alerts })
        return response?.data ?? null
    } catch {
        return null
    }
}
